/**
 * A message box you can name people in (docs/03 §3e, punch item 3.2).
 *
 * Matt named @mentions as the thing v1 got worst, and chose the familiar
 * composer: typing `@` opens a chooser, and picking somebody inserts their name
 * and records their USER ID.
 *
 * THE ID IS THE MENTION. The body is never re-read to work out who "@Rob" meant
 * — two people called Rob and a rename both break that, and a notification sent
 * to the wrong person is worse than none. So this component reports two things
 * to its caller: the text somebody wrote, and the ids they chose. The words are
 * just words afterwards, which is also why the body needs no markup and stays
 * legible in search, exports and audit diffs.
 *
 * One text match remains, at SUBMIT only, and it subtracts rather than adds: an
 * id is dropped when its "@Name" is no longer in the text, so deleting the
 * mention deletes the mention. It can never invent one.
 *
 * The chooser is a listbox the textarea POINTS AT, rather than a `<datalist>`
 * or a full ARIA combobox. A datalist cannot be filtered on a substring of a
 * textarea's value and cannot be driven with the arrow keys from inside one;
 * and a combobox is the wrong answer here for two reasons that agree —
 * ARIA-in-HTML allows a `<textarea>` no role but `textbox`, and `aria-expanded`
 * is not a state `textbox` supports, so declaring both would be invalid twice
 * over AND would take `textbox` away from everything that looks for it.
 *
 * So the textarea keeps its own role and the supported halves of the pattern —
 * `aria-autocomplete`, `aria-controls`, `aria-activedescendant` — and a polite
 * live region says how many people matched, which is the part `aria-expanded`
 * would otherwise have carried.
 */

import { clsx } from 'clsx';
import {
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

export interface MentionOption {
  readonly id: string;
  readonly label: string;
}

/**
 * The `@partial` immediately before the caret, or null.
 *
 * Anchored to a word boundary so an email address does not open the chooser
 * every time somebody types one, and stopped at a newline so a `@` two
 * paragraphs up cannot match. Exported for its unit test: the parsing is the
 * part with edge cases, and it is a pure function so it can be tested as one.
 *
 * Up to two INTERNAL spaces, because people are called "Rob Nunez" and
 * "Mary Beth O'Neill" — a query that stopped at the first space could only ever
 * filter on a first name, which in a team with two Robs is the one case the
 * chooser exists for. Two and no more, so an unmatched `@` mid-sentence stops
 * matching within a few words rather than trailing a whole paragraph.
 *
 * A query of nothing but spaces is nothing: "email me @ " must not open a
 * chooser listing everybody whose name happens to contain a space.
 */
export function mentionQueryAt(text: string, caret: number): string | null {
  const before = text.slice(0, caret);
  const match = /(^|[\s(])@([\p{L}\p{M}'’.-]{0,40}(?: [\p{L}\p{M}'’.-]{0,40}){0,2})$/u.exec(before);
  if (match === null) return null;
  const query = match[2] ?? '';
  return query !== '' && query.trim() === '' ? null : query;
}

/** Which chosen ids are still written in the text. Subtractive by design. */
export function survivingMentions(
  text: string,
  chosen: readonly MentionOption[],
): readonly string[] {
  return chosen.filter((one) => text.includes(`@${one.label}`)).map((one) => one.id);
}

export function MentionTextArea({
  label,
  value,
  onChange,
  options,
  chosen,
  onChosenChange,
  rows = 3,
  maxLength,
  hint,
  disabled,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (next: string) => void;
  /** Everybody who could be named. Filtered here, never on the server. */
  readonly options: readonly MentionOption[];
  readonly chosen: readonly MentionOption[];
  readonly onChosenChange: (next: readonly MentionOption[]) => void;
  readonly rows?: number;
  readonly maxLength?: number;
  readonly hint?: string;
  readonly disabled?: boolean;
}): ReactNode {
  const id = useId();
  const listId = `${id}-mentions`;
  const box = useRef<HTMLTextAreaElement>(null);
  const [query, setQuery] = useState<string | null>(null);
  const [active, setActive] = useState(0);

  /**
   * THE CARET OWED BY THE LAST MENTION INSERTION — with the exact value it
   * belongs to, because an offset on its own is a number that outlives its
   * reason.
   *
   * RM-079 / docs/11 X-13. `choose()` used to schedule this through
   * `requestAnimationFrame`, which is a promise about the NEXT PAINT and not
   * about the next keystroke. A frame is 16ms; a person choosing a name and
   * immediately selecting-all-and-typing beats that, and so does a loaded test
   * runner. When the frame callback landed AFTER the next interaction it did
   * exactly what it was told a moment too late: collapsed a live selection back
   * to just after the name, so the typing that should have REPLACED the message
   * appended to it instead. The posted note read
   * "Can @Rob Nunez Can @Rob Nunez confirm the meter reading?".
   *
   * A ref and not state: nothing renders from it, and a second render to move a
   * caret would be the same lateness with more steps.
   */
  const owedCaret = useRef<{ readonly value: string; readonly at: number } | null>(null);

  /**
   * Place that caret once, in the commit that carries its value.
   *
   * A layout effect runs after React has put the value in the DOM and before
   * the browser paints — NOT necessarily before `choose()` returns, which is a
   * different and weaker claim. What matters is the ordering against INPUT: a
   * discrete event flushes synchronously, so this runs before the browser can
   * deliver the next keystroke, and nothing is left queued for it to land on.
   *
   * Three refusals, in order, and the ref is emptied before any of them so a
   * request can never be applied twice or survive into a later render:
   *
   *   * no request — every ordinary edit stops here, and the caret is left
   *     exactly where the person put it;
   *   * the field is gone, or holds a different value — a newer edit already
   *     won, and this offset is measured against text that no longer exists;
   *   * focus has moved on — the author is somewhere else now, and pulling it
   *     back is the same theft one frame earlier.
   */
  useLayoutEffect(() => {
    const owed = owedCaret.current;
    if (owed === null) return;
    owedCaret.current = null;
    const element = box.current;
    if (element?.value !== owed.value) return;
    if (document.activeElement !== element) return;
    element.setSelectionRange(owed.at, owed.at);
  });

  const matches =
    query === null
      ? []
      : options.filter((one) => one.label.toLowerCase().includes(query.toLowerCase())).slice(0, 6);
  const open = matches.length > 0;

  const refresh = (next: string, caret: number): void => {
    setQuery(mentionQueryAt(next, caret));
    setActive(0);
  };

  /** Replace the `@partial` under the caret with the chosen name. */
  const choose = (option: MentionOption): void => {
    const element = box.current;
    if (element === null) return;
    const caret = element.selectionStart;
    // What to remove is exactly what opened the chooser, so it is measured with
    // the same function rather than a second regex — the two drifted apart once
    // already, and the result was the partial left sitting in front of the
    // inserted name.
    const partial = mentionQueryAt(value, caret);
    if (partial === null) return;
    const before = value.slice(0, caret - partial.length - 1);
    const after = value.slice(caret);
    const inserted = `@${option.label} `;
    const next = `${before}${inserted}${after}`;
    // The caret belongs after the name, not at the end of the message: people
    // mention somebody mid-sentence and keep typing. Claimed BEFORE the value
    // is handed over, so the commit that carries the text carries the caret.
    owedCaret.current = { value: next, at: before.length + inserted.length };
    onChange(next);
    if (!chosen.some((one) => one.id === option.id)) onChosenChange([...chosen, option]);
    setQuery(null);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (!open) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((current) => (current + 1) % matches.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((current) => (current - 1 + matches.length) % matches.length);
    } else if (event.key === 'Enter' || event.key === 'Tab') {
      const option = matches[active];
      if (option !== undefined) {
        // Enter picks the highlighted person rather than breaking the line —
        // the behaviour every other mention box has, and only while the
        // chooser is open.
        event.preventDefault();
        choose(option);
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setQuery(null);
    }
  };

  const used = value.length;
  const nearLimit = maxLength !== undefined && used > maxLength * 0.8;

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-meta font-medium text-ink">
        {label}
      </label>
      <div className="relative">
        <textarea
          id={id}
          ref={box}
          rows={rows}
          maxLength={maxLength}
          value={value}
          disabled={disabled}
          // ADR-40: every form control is skipped by password managers.
          data-keeper-ignore
          aria-controls={open ? listId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={open ? `${listId}-${String(active)}` : undefined}
          aria-describedby={`${id}-hint`}
          onChange={(event) => {
            onChange(event.target.value);
            refresh(event.target.value, event.target.selectionStart);
          }}
          onKeyDown={onKeyDown}
          onBlur={() => {
            // Not immediately: a click on an option blurs the textarea first,
            // and closing here would remove the option before it is chosen.
            setTimeout(() => {
              setQuery(null);
            }, 150);
          }}
          className="keeper-ignore min-h-11 w-full resize-y rounded-ctl border border-control-line bg-card px-3 py-2 text-body leading-relaxed text-ink placeholder:text-ink2 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-700"
        />
        {open ? (
          <ul
            id={listId}
            role="listbox"
            aria-label="People you can mention"
            className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-ctl border border-card-line bg-card py-1 shadow-lg"
          >
            {matches.map((option, index) => (
              <li
                key={option.id}
                id={`${listId}-${String(index)}`}
                role="option"
                aria-selected={index === active}
                // A listbox option is not focusable; the textarea keeps focus
                // and points at this row with aria-activedescendant.
                onMouseDown={(event) => {
                  event.preventDefault();
                  choose(option);
                }}
                onMouseEnter={() => {
                  setActive(index);
                }}
                className={clsx(
                  'cursor-pointer px-3 py-2 text-body',
                  index === active ? 'bg-surface-hover text-ink' : 'text-ink2',
                )}
              >
                {option.label}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      {/* What `aria-expanded` would have said, in the one way a textbox can say
          it. Polite: it must not interrupt somebody mid-sentence. */}
      <p role="status" aria-live="polite" className="sr-only">
        {open
          ? `${String(matches.length)} ${matches.length === 1 ? 'person' : 'people'} to choose from. Use the arrow keys.`
          : ''}
      </p>
      <p id={`${id}-hint`} className="text-meta text-ink2">
        {nearLimit
          ? `${String(used)} / ${String(maxLength)} characters`
          : (hint ?? 'Type @ to name somebody. They will be told, and can answer here.')}
      </p>
    </div>
  );
}
